import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TablePagination,
} from "@mui/material";
import styled from "@emotion/styled";
import { tableCellClasses } from "@mui/material/TableCell";
import { getServerLogs } from "../redux/actions/AdminAction";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#282828",
    color: "#ffffff",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#e8e9eb",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function DisplayLogs() {
  const dispatch = useDispatch();
  const [logs, setLogs] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedLogType, setSelectedLogType] = useState("all");
  const { logsInfo, loading, error } = useSelector((state) => state.GetLogs);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(getServerLogs("", selectedLogType));
  }, [dispatch, selectedLogType]);

  useEffect(() => {
    if (logsInfo) {
      setLogs(logsInfo);
    }
  }, [logsInfo]);

  // Filter logs based on search text
  const filteredLogs = logs.filter(
    (log) =>
      log.message?.toLowerCase().includes(searchText.toLowerCase()) ||
      log.module?.toLowerCase().includes(searchText.toLowerCase()) ||
      log.created_by?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Apply pagination
  const paginatedLogs = filteredLogs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Container>
      <Box>
        <Box
          sx={{
            margin: 3,
            fontSize: 25,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">Server Logs</Typography>

          <FormControl sx={{ width: "200px", mb: 3, mx: 2 }}>
            <InputLabel>Log Type</InputLabel>
            <Select
              value={selectedLogType}
              label="Log Type"
              onChange={(e) => setSelectedLogType(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="sc_rule">Schedule Change</MenuItem>
              <MenuItem value="hx_rule">HX Killer</MenuItem>
              <MenuItem value="invoice_report">Invoice Generation</MenuItem>
              <MenuItem value="agent_report">Agency Booking Report</MenuItem>
              <MenuItem value="multi_pcc_log">Ticket Report</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider sx={{ marginTop: 2 }} />
      </Box>

      {/* Search field */}
      <Box sx={{ mb: 3, width: "90%", mx: "auto" }}>
        <TextField
          label="Search logs..."
          variant="outlined"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>

      <Box
        sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 5 }}
      >
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Paper sx={{ width: "90%", overflow: "hidden" }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>No.</StyledTableCell>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>Level</StyledTableCell>
                    <StyledTableCell>Message</StyledTableCell>
                    <StyledTableCell>Module</StyledTableCell>
                    <StyledTableCell>User</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLogs?.length > 0 ? (
                    paginatedLogs.map((log, index) => (
                      <StyledTableRow key={log.id}>
                        <StyledTableCell>
                          {page * rowsPerPage + index + 1}
                        </StyledTableCell>
                        <StyledTableCell>
                          {new Date(log.created_at).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })}
                        </StyledTableCell>
                        <StyledTableCell>{log.level}</StyledTableCell>
                        <StyledTableCell>{log.message}</StyledTableCell>
                        <StyledTableCell>{log.module}</StyledTableCell>
                        <StyledTableCell>
                          {log.created_by || "System"}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell colSpan={6} align="center">
                        No logs found
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredLogs.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              sx={{ mt: 1, mb: 3 }}
            />
          </Paper>
         
        )}
      </Box>
    </Container>
  );
}
